import {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import screenfull from "screenfull";
import {AppDispatch, RootState} from "../../store";
import {
    setDuration,
    setFullscreen,
    setPlay,
    setProgress,
    closeVideoPlayer,
} from "../../store/player/playerSlice";
import Keys from "../../constants/helper";
import ReactPlayer from "react-player";
import useResize from "../useResize";
import {useLocation, useNavigate} from "react-router-dom";
import {
    contentQueries,
    useContinueWatchingListMutation,
    useDeleteWatchingItemMutation,
    useLazyGetContentDetailsQuery,
    useLazyGetValidateTokenQuery,
} from "../../api/modules";
import {
    MOBILE_VERSION, TABLET_VERSION,
    LIVE_STALL_RELOAD_COOLDOWN_MS,
    TOKEN_VALIDATION_INTERVAL_MS,
    WATCHED_COMPLETED_THRESHOLD,
} from "../../constants";
import {routes} from "../../routes/routes";
import {ProgramPages} from "../../types";
import {ApiTags} from "../../api/config";
import {getClientIp} from "../../utils";

const getVideoElement = (playerRef: React.RefObject<ReactPlayer | null>): HTMLVideoElement | null => {
    const internal = playerRef.current?.getInternalPlayer?.() as HTMLVideoElement | null;
    if (internal instanceof HTMLVideoElement) return internal;
    return (playerRef.current as any)?.wrapper?.querySelector?.("video") ?? null;
};

const isIOS = (() => {
    if (typeof window === "undefined") return false;
    return /iP(hone|ad|od)/.test(navigator.userAgent);
})();

const isIOSIPhone = (() => {
    if (typeof window === "undefined") return false;
    return /iPhone/.test(navigator.userAgent);
})();

const isIOSSafari = (() => {
    if (typeof window === "undefined") return false;
    const ua = navigator.userAgent;
    const isSafari = ua.includes("Safari") && !ua.includes("Chrome") && !ua.includes("Android");
    return isIOS || isSafari;
})();

const FAKE_FULLSCREEN_CLASS = "fakeFullscreen";
const isRealtimeContentType = (value?: string): value is typeof ProgramPages.LIVE | typeof ProgramPages.SPORTS =>
    value === ProgramPages.LIVE || value === ProgramPages.SPORTS;

const prepareVideoForIOSFullscreen = (video: HTMLVideoElement): Promise<void> => {
    return new Promise((resolve) => {
        // Step 1: force AirPlay attributes
        video.setAttribute("x-webkit-airplay", "allow");
        video.setAttribute("airplay", "allow");
        (video as any).webkitAirPlayAllowed = true;
        (video as any).disableRemotePlayback = false;
        video.removeAttribute("disableremoteplayback");
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        (video as any).playsInline = true;

        // Step 2: ensure the element has real pixel dimensions.
        // Override any CSS that might be collapsing it to 0×0.
        const prevWidth = video.style.width;
        const prevHeight = video.style.height;
        const prevPosition = video.style.position;

        const rect = video.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
            // Temporarily give it real screen dimensions so Safari sees a "video"
            video.style.width = `${window.innerWidth}px`;
            video.style.height = `${window.innerHeight}px`;
            video.style.position = "fixed";
        }

        // Step 3: wait one animation frame so the browser paints the new size
        // before we call webkitEnterFullscreen
        requestAnimationFrame(() => {
            // Restore original styles — the native fullscreen takes over sizing anyway
            video.style.width = prevWidth;
            video.style.height = prevHeight;
            video.style.position = prevPosition;
            resolve();
        });
    });
};

export const useVideoPlayer = (
    id: number | null,
    type: string | undefined,
    isVideoPlayerOpen: boolean,
    isMobileSerial?: boolean
) => {
    const {innerWidth} = useResize();
    const isMobile = innerWidth < MOBILE_VERSION;
    const isTabletOrMobile = innerWidth < TABLET_VERSION;
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const playerRef = useRef<ReactPlayer | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const isClosingRef = useRef(false);
    const seekRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Set by useIosFullscreen when exiting native fullscreen; blocks handleSeekToResume
    // from seeking back to the API-saved minute while we restore the QuickTime exit position.
    const iosExitSeekRef = useRef<number | null>(null);
    const wakeLockRef = useRef<any>(null);
    const profileID = localStorage.getItem(Keys.EPIC_PROFILE_ID) as string;
    const [loading, setLoading] = useState<boolean>(true);
    const [liveReloadNonce, setLiveReloadNonce] = useState(0);
    const [hasStreamStarted, setHasStreamStarted] = useState(false);

    const playRef = useRef(false);
    const cleanupVideoListenersRef = useRef<(() => void) | null>(null);
    const lastLiveTimeRef = useRef(0);
    const lastLiveReloadAtRef = useRef(0);

    const [continueWatchingList] = useContinueWatchingListMutation();
    const [deleteWatchingItem] = useDeleteWatchingItemMutation();
    const [getContentDetails] = useLazyGetContentDetailsQuery();
    const [validateToken] = useLazyGetValidateTokenQuery();

    const {play, isFullscreen, progress, serialData, enabledResume, watchingAtMinute} = useSelector(
        (state: RootState) => state.videoPlayer
    );

    useEffect(() => {
        playRef.current = play;
    }, [play]);

    const validationInFlightRef = useRef(false);

    const handleSeekToResume = (
        minuteToSeek: number,
        attempt = 0,
        maxAttempts = 10,
    ) => {
        // Suppress API-position resume while an iOS fullscreen exit seek is pending.
        if (iosExitSeekRef.current !== null) return;
        if (minuteToSeek <= 0) return;
        const seekSeconds = minuteToSeek * 60;
        const currentTime = playerRef.current?.getCurrentTime?.() ?? 0;
        if (Math.abs(currentTime - seekSeconds) < 2) return;
        try {
            playerRef.current?.seekTo(seekSeconds, "seconds");
        } catch (error) {
            void error;
        }
        if (attempt >= maxAttempts) return;
        if (seekRetryTimeoutRef.current) clearTimeout(seekRetryTimeoutRef.current);
        seekRetryTimeoutRef.current = setTimeout(() => {
            const updatedTime = playerRef.current?.getCurrentTime?.() ?? 0;
            if (Math.abs(updatedTime - seekSeconds) > 2) {
                handleSeekToResume(minuteToSeek, attempt + 1, maxAttempts);
            }
        }, 250);
    };

    const requestWakeLock = async () => {
        if (!isVideoPlayerOpen || !play) return;
        if (!("wakeLock" in navigator) || wakeLockRef.current) return;
        try {
            wakeLockRef.current = await (navigator as any).wakeLock.request("screen");
            wakeLockRef.current?.addEventListener?.("release", () => {
                wakeLockRef.current = null;
            });
            document.addEventListener("visibilitychange", async () => {
                if (document.visibilityState === "visible" && !wakeLockRef.current && play && isVideoPlayerOpen) {
                    try {
                        wakeLockRef.current = await (navigator as any).wakeLock.request("screen");
                    } catch {
                        void 0;
                    }
                }
            });
        } catch (error) {
            console.warn("WakeLock request failed:", error);
        }
    };

    const releaseWakeLock = async () => {
        if (!wakeLockRef.current) return;
        try {
            await wakeLockRef.current.release();
        } catch {
            void 0;
        } finally {
            wakeLockRef.current = null;
        }
    };

    const exitFakeFullscreen = () => {
        if (!containerRef.current) return;
        containerRef.current.classList.remove(FAKE_FULLSCREEN_CLASS);
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";
    };

    useEffect(() => {
        if (!isTabletOrMobile) {
            releaseWakeLock();
            return;
        }
        if (isVideoPlayerOpen && play) {
            requestWakeLock();
        } else {
            releaseWakeLock();
        }
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible" && isVideoPlayerOpen && play) {
                requestWakeLock();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            releaseWakeLock();
        };
    }, [isTabletOrMobile, isVideoPlayerOpen, play]);

    useEffect(() => {
        if (isVideoPlayerOpen) dispatch(setPlay(true));
    }, [isVideoPlayerOpen, dispatch]);

    useEffect(() => {
        if (!isVideoPlayerOpen || !play || !hasStreamStarted) return;
        if (!localStorage.getItem(Keys.EPIC_JWT)) return;
        let intervalId: ReturnType<typeof setInterval> | null = null;
        let isCancelled = false;
        const runValidation = async () => {
            if (isCancelled || validationInFlightRef.current) return;
            validationInFlightRef.current = true;
            try {
                const shouldSendPlaybackContext = isRealtimeContentType(type) && id;
                const clientIp = shouldSendPlaybackContext ? await getClientIp() : null;
                await validateToken(shouldSendPlaybackContext
                    ? {content: id, client_ip: clientIp}
                    : undefined
                ).unwrap();
            } catch (error) {
                void error;
            } finally {
                validationInFlightRef.current = false;
            }
        };
        runValidation();
        intervalId = setInterval(runValidation, TOKEN_VALIDATION_INTERVAL_MS);
        return () => {
            isCancelled = true;
            if (intervalId) clearInterval(intervalId);
        };
    }, [id, type, isVideoPlayerOpen, play, hasStreamStarted, validateToken]);

    useEffect(() => {
        if (!screenfull.isEnabled) return;
        const onFullscreenChange = () => {
            const nowFullscreen = screenfull.isFullscreen;
            dispatch(setFullscreen(nowFullscreen));
            if (!nowFullscreen && playRef.current) {
                const video = getVideoElement(playerRef);
                if (video) {
                    setTimeout(() => {
                        video.play().catch(() => void 0);
                    }, 100);
                }
            }
        };
        screenfull.on("change", onFullscreenChange);
        return () => screenfull.off("change", onFullscreenChange);
    }, [dispatch]);

    const attachVideoListeners = useCallback((video: HTMLVideoElement) => {
        const onVideoPause = () => {
            requestAnimationFrame(() => {
                if (video.paused && !video.ended && !isClosingRef.current) {
                    dispatch(setPlay(false));
                }
            });
        };

        const onVideoPlay = () => {
            if (!isClosingRef.current) {
                dispatch(setPlay(true));
            }
        };

        video.addEventListener("pause", onVideoPause);
        video.addEventListener("play", onVideoPlay);

        return () => {
            video.removeEventListener("pause", onVideoPause);
            video.removeEventListener("play", onVideoPlay);
        };
    }, [dispatch]);

    useEffect(() => {
        return () => {
            cleanupVideoListenersRef.current?.();
        };
    }, []);

    const handleReady = useCallback(() => {
        setLoading(false);

        cleanupVideoListenersRef.current?.();

        const video = getVideoElement(playerRef);
        if (!video) return;

        cleanupVideoListenersRef.current = attachVideoListeners(video);

        if (enabledResume && watchingAtMinute > 0) {
            handleSeekToResume(watchingAtMinute);
        }
    }, [setLoading, attachVideoListeners, enabledResume, watchingAtMinute]);

    const togglePlayState = () => {
        const nextPlay = !play;
        dispatch(setPlay(nextPlay));

        // iPhone Safari often requires a direct user-gesture play() call
        // in addition to state-based playback toggling.
        if (isIOSIPhone) {
            const video = getVideoElement(playerRef);
            if (video) {
                if (nextPlay) {
                    video.play().catch(() => void 0);
                } else {
                    video.pause();
                }
            }
        }

        if (isMobile && playerRef.current && !isMobileSerial) {
            if (screenfull.isEnabled && !isFullscreen && containerRef.current) {
                screenfull.request(containerRef.current).catch((error) => {
                    void error;
                });
            }
        }
    };

    const handleProgress = (state: { played: number; playedSeconds?: number }) => {
        const hasTimeProgress = (state.playedSeconds ?? 0) > 0 || state.played > 0;
        if (!hasStreamStarted && hasTimeProgress) {
            setHasStreamStarted(true);
        }
        dispatch(setProgress(state.played * 100));
    };

    useEffect(() => {
        if (!isVideoPlayerOpen) {
            setHasStreamStarted(false);
        }
    }, [isVideoPlayerOpen]);

    useEffect(() => {
        if (!isRealtimeContentType(type) || !isVideoPlayerOpen || !play) return;
        const intervalId = setInterval(() => {
            const currentVideoTime = playerRef.current?.getCurrentTime?.() ?? 0;
            const isStalled = currentVideoTime > 0 && Math.abs(currentVideoTime - lastLiveTimeRef.current) < 0.15;
            const now = Date.now();
            if (isStalled && now - lastLiveReloadAtRef.current > LIVE_STALL_RELOAD_COOLDOWN_MS) {
                lastLiveReloadAtRef.current = now;
                setLiveReloadNonce((prev) => prev + 1);
            }
            lastLiveTimeRef.current = currentVideoTime;
        }, 15000);
        return () => clearInterval(intervalId);
    }, [type, isVideoPlayerOpen, play]);

    const handleDuration = (duration: number) => {
        dispatch(setDuration(duration));
    };

    const handleSeek = (newTime: number) => {
        if (playerRef.current) playerRef.current.seekTo(newTime);
    };

    const handleFullscreenChange = () => {
        if (screenfull.isEnabled) dispatch(setFullscreen(screenfull.isFullscreen));
    };

    const toggleFullscreen = async () => {
        const video = getVideoElement(playerRef);

        if (isIOS && video) {
            const nativeVideo = video as HTMLVideoElement & {
                webkitEnterFullscreen?: () => void;
                webkitExitFullscreen?: () => void;
                webkitDisplayingFullscreen?: boolean;
            };

            try {
                if (nativeVideo.webkitDisplayingFullscreen) {
                    nativeVideo.webkitExitFullscreen?.();
                    dispatch(setFullscreen(false));
                } else {
                    // KEY FIX: prepare dimensions + AirPlay attributes BEFORE
                    // calling webkitEnterFullscreen. Safari reads the video element's
                    // rendered size at the moment of this call to decide whether to
                    // offer video AirPlay or audio-only AirPlay.
                    await prepareVideoForIOSFullscreen(video);
                    nativeVideo.webkitEnterFullscreen?.();
                    dispatch(setFullscreen(true));
                }
            } catch {
                void 0;
            }
            return;
        }

        if (!containerRef.current || !screenfull.isEnabled) return;

        try {
            await screenfull.toggle(containerRef.current);

            const nowFullscreen = screenfull.isFullscreen;
            dispatch(setFullscreen(nowFullscreen));

            if ("orientation" in screen) {
                if (nowFullscreen) {
                    await screen.orientation.lock("landscape").catch(() => void 0);
                } else {
                    screen.orientation.unlock?.();
                }
            }
        } catch {
            void 0;
        }
    };

    const handleClose = async () => {
        if (isClosingRef.current) return;
        isClosingRef.current = true;
        await releaseWakeLock();

        const currentTime = playerRef.current?.getCurrentTime?.() || 0;
        const currentMinute = Math.floor(currentTime / 60);
        const snapshot = {
            id,
            type,
            profileID,
            progress,
            serialData,
            currentMinute,
        };

        try {
            if (isIOSSafari) {
                dispatch(setFullscreen(false));
            } else if (screenfull.isEnabled && screenfull.isFullscreen) {
                await screenfull.exit();
            }
        } catch (error) {
            void error;
        }

        if (
            snapshot.type !== "LIVE" &&
            snapshot.profileID &&
            snapshot.id
        ) {
            try {
                const isSeries = snapshot.type === "SERIE";
                const isLastSeriesEpisode =
                    isSeries &&
                    !!snapshot.serialData?.seasons?.length &&
                    (() => {
                        const currentSeasonIndex =
                            (snapshot.serialData?.seasonNumber || 1) - 1;
                        const currentEpisodeIndex =
                            (snapshot.serialData?.episodeNumber || 1) - 1;
                        const seasons = snapshot.serialData?.seasons || [];
                        const currentSeason = seasons[currentSeasonIndex];
                        if (!currentSeason) return false;
                        const isLastSeason = currentSeasonIndex === seasons.length - 1;
                        const isLastEpisode = currentEpisodeIndex === currentSeason.chapters.length - 1;
                        return isLastSeason && isLastEpisode;
                    })();
                const shouldDeleteWatching =
                    snapshot.progress >= WATCHED_COMPLETED_THRESHOLD &&
                    (!isSeries || isLastSeriesEpisode);
                const clientIp = await getClientIp();

                if (shouldDeleteWatching) {
                    await deleteWatchingItem({
                        params: {profileID: snapshot.profileID},
                        body: {content: snapshot.id, client_ip: clientIp},
                    }).unwrap();
                } else {
                    await continueWatchingList({
                        params: {profileID: snapshot.profileID},
                        body: {
                            content: snapshot.id,
                            minute: snapshot.currentMinute,
                            client_ip: clientIp,
                            ...(isSeries &&
                            snapshot.serialData?.seasonNumber &&
                            snapshot.serialData?.episodeNumber
                                ? {
                                    season_number: snapshot.serialData.seasonNumber,
                                    chapter_number: snapshot.serialData.episodeNumber,
                                }
                            : {}),
                        },
                    }).unwrap();
                }
                dispatch(contentQueries.util.invalidateTags([
                    {type: ApiTags.VIDEO, id: String(snapshot.id)},
                ]));
            } catch (error) {
                void error;
            }
        }

        dispatch(closeVideoPlayer());
        if (snapshot.type === "SERIE" && snapshot.id) {
            navigate(`${routes.details.details_serials}/${snapshot.id}`, {
                state: {
                    seasonNumber: snapshot.serialData?.seasonNumber ?? null,
                    episodeNumber: snapshot.serialData?.episodeNumber ?? null,
                },
            });
        } else if (snapshot.type === "MOVIE" && snapshot.id) {
            const detailsPath = `${routes.details.details}/${snapshot.id}`;
            if (location.pathname !== detailsPath) {
                navigate(detailsPath, {
                    state: {
                        fromPath: `${location.pathname}${location.search}`,
                    },
                });
            }
        }
        isClosingRef.current = false;
    };

    useEffect(() => {
        if (!isVideoPlayerOpen || !enabledResume || watchingAtMinute <= 0) return;
        const timer = setTimeout(() => handleSeekToResume(watchingAtMinute), 300);
        return () => {
            clearTimeout(timer);
            if (seekRetryTimeoutRef.current) clearTimeout(seekRetryTimeoutRef.current);
        };
    }, [isVideoPlayerOpen, enabledResume, watchingAtMinute]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const fetchLatestWatchingAndSeek = async () => {
            if (!isVideoPlayerOpen || !id || type === "LIVE" || !profileID || !enabledResume) return;
            try {
                const role = localStorage.getItem(Keys.EPIC_USER_ROLE);
                const detail = await getContentDetails({id, role, profile_id: profileID}, false).unwrap();
                const apiWatchingMinute = Number(detail?.watching_at_minute ?? 0);
                if (apiWatchingMinute <= 0) return;
                if (type === "SERIE") {
                    const sameEpisode =
                        Number(detail?.watching_season) === Number(serialData?.seasonNumber) &&
                        Number(detail?.watching_chapter) === Number(serialData?.episodeNumber);
                    if (!sameEpisode) return;
                }
                timeoutId = setTimeout(() => handleSeekToResume(apiWatchingMinute), 300);
            } catch (error) {
                void error;
            }
        };
        fetchLatestWatchingAndSeek();
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            if (seekRetryTimeoutRef.current) clearTimeout(seekRetryTimeoutRef.current);
        };
    }, [
        isVideoPlayerOpen,
        id,
        type,
        profileID,
        enabledResume,
        serialData?.seasonNumber,
        serialData?.episodeNumber,
        getContentDetails,
    ]);

    return {
        playerRef,
        containerRef,
        loading,
        setLoading,
        iosExitSeekRef,
        handleReady,
        togglePlayState,
        handleProgress,
        handleDuration,
        handleSeek,
        handleFullscreenChange,
        toggleFullscreen,
        handleClose,
        liveReloadNonce,
    };
};
